import {type RefObject, useCallback, useEffect, useRef} from 'react';
import {flushSync} from 'react-dom';
import {useSelector} from 'react-redux';

import {TIPS_NAVIGATE_TO_GROUP_EVENT} from '@/hooks/useEvents';
import {NAVIGATION_DIRECTION} from '@/navigation/navigationDirection';
import {usePageTransition} from '@/navigation/usePageTransition';
import type {TipsItem} from '@/services/content/tipsContent.types';
import eventMessagesService from '@/services/events/eventMessagesService';
import {selectIsDeepLinkEntry, selectIsEmbedded} from '@/state/selectors/app';
import PopupPageView from '../views/PopupPageView';
import TipsListView from '../views/TipsListView';
import {saveTipsListScrollTop} from './useTipsListScrollRestore';

interface UseTipsNavigationParams {
    cards: TipsItem[];
    deepLinkCard: TipsItem | undefined;
    listViewportRef: RefObject<HTMLDivElement | null>;
    listScrollTopRef: RefObject<number>;
}

/**
 * Owns the transition stack and the two entry points into it:
 *   - clicking a card on the list (handleCardSelect)
 *   - the host asking to jump straight to a group (UWM_NAVIGATE_TO_GROUP event)
 */
const useTipsNavigation = ({cards, deepLinkCard, listViewportRef, listScrollTopRef}: UseTipsNavigationParams) => {
    const isEmbedded = useSelector(selectIsEmbedded);
    const isDeepLinkEntry = useSelector(selectIsDeepLinkEntry);

    // Stable refs for callbacks that the navigate() output captures - avoids stale closures.
    const onCardSelectRef = useRef<(card: TipsItem, fromDeepLink?: boolean) => void>(null!);
    const onBackRef = useRef<() => void>(null!);

    const sendHostBack = useCallback(() => eventMessagesService.sendBackButtonTriggerMessage(), []);

    const renderList = useCallback(
        () => <TipsListView isEmbedded={isEmbedded} onCardSelect={(card) => onCardSelectRef.current(card)} />,
        [isEmbedded]
    );

    // Kept in one place so all three call sites (initial deep-link, card click, navigate-to-group)
    // stay in sync on where Back should go.
    const buildPopupView = useCallback(
        (card: TipsItem, {backToHost}: {backToHost: boolean}) => (
            <PopupPageView
                card={card}
                isEmbedded={isEmbedded}
                onBack={backToHost ? sendHostBack : () => onBackRef.current()}
            />
        ),
        [isEmbedded, sendHostBack]
    );

    const transitionProps = usePageTransition(
        deepLinkCard ? buildPopupView(deepLinkCard, {backToHost: true}) : renderList()
    );
    const {navigate, replace} = transitionProps;

    const handleBack = useCallback(() => {
        navigate(renderList(), NAVIGATION_DIRECTION.BACKWARD);
    }, [navigate, renderList]);

    const handleCardSelect = useCallback(
        (card: TipsItem, fromDeepLink = false) => {
            saveTipsListScrollTop(listViewportRef, listScrollTopRef);

            const backToHost = fromDeepLink && isDeepLinkEntry;

            navigate(buildPopupView(card, {backToHost}), NAVIGATION_DIRECTION.FORWARD);
        },
        [navigate, isDeepLinkEntry, buildPopupView, listViewportRef, listScrollTopRef]
    );

    useEffect(() => {
        // The host slides the module in itself, so swap without our own slide (it would play on top).
        // A missing group falls back to the list. Commit before LOADING_END so the host overlay lifts onto it.
        const onNavigateToGroup = (event: Event) => {
            const {groupType} = (event as CustomEvent<{groupType?: string}>).detail || {};
            const targetCard = groupType ? cards.find((card) => card.guideItemType === groupType) : undefined;

            flushSync(() => replace(targetCard ? buildPopupView(targetCard, {backToHost: true}) : renderList()));
            eventMessagesService.sendModuleLoadingEnd();
        };

        document.addEventListener(TIPS_NAVIGATE_TO_GROUP_EVENT, onNavigateToGroup);

        return () => document.removeEventListener(TIPS_NAVIGATE_TO_GROUP_EVENT, onNavigateToGroup);
    }, [cards, replace, renderList, buildPopupView]);

    // Publish the latest handlers so anything captured through the refs above (usePageTransition
    // initialView, TipsListView's onCardSelect) always dispatches to the current versions.
    onCardSelectRef.current = handleCardSelect;
    onBackRef.current = handleBack;

    return {handleCardSelect, transitionProps};
};

export default useTipsNavigation;
